import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { getOtpDigitBoxStyles, otpContainerStyles } from './styled';
import type { OtpInputProps } from './types';

const OTP_LENGTH = 6;

const OtpInput: FC<OtpInputProps> = ({ value, onChange, disabled = false, hasError = false }) => {
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	const focusInput = useCallback((index: number) => {
		if (index >= 0 && index < OTP_LENGTH) {
			inputRefs.current[index]?.focus();
		}
	}, []);

	useEffect(() => {
		if (!disabled) {
			focusInput(0);
		}
	}, [disabled, focusInput]);

	const handleChange = useCallback(
		(index: number, inputValue: string) => {
			if (disabled) return;

			const digit = inputValue.replace(/\D/g, '').slice(-1);

			const newValue = value.split('');
			newValue[index] = digit;
			const updatedValue = newValue.join('').slice(0, OTP_LENGTH);

			onChange(updatedValue);

			if (digit && index < OTP_LENGTH - 1) {
				focusInput(index + 1);
			}
		},
		[value, onChange, disabled, focusInput]
	);

	const handleKeyDown = useCallback(
		(index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
			if (disabled) return;

			if (e.key === 'Backspace') {
				e.preventDefault();
				const newValue = value.split('');

				if (newValue[index]) {
					newValue[index] = '';
					onChange(newValue.join(''));
				} else if (index > 0) {
					newValue[index - 1] = '';
					onChange(newValue.join(''));
					focusInput(index - 1);
				}
			} else if (e.key === 'ArrowLeft' && index > 0) {
				e.preventDefault();
				focusInput(index - 1);
			} else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
				e.preventDefault();
				focusInput(index + 1);
			}
		},
		[value, onChange, disabled, focusInput]
	);

	const handlePaste = useCallback(
		(e: React.ClipboardEvent<HTMLInputElement>) => {
			if (disabled) return;

			e.preventDefault();
			const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);

			if (pastedData) {
				onChange(pastedData);
				const nextIndex = Math.min(pastedData.length, OTP_LENGTH - 1);
				focusInput(nextIndex);
			}
		},
		[onChange, disabled, focusInput]
	);

	const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select();
	}, []);

	return (
		<div
			style={otpContainerStyles}
			role='group'
			aria-label='One-time password input'
		>
			{Array.from({ length: OTP_LENGTH }).map((_, index) => (
				<input
					key={index}
					ref={(el: HTMLInputElement | null) => {
						inputRefs.current[index] = el;
					}}
					type='text'
					inputMode='numeric'
					autoComplete='one-time-code'
					maxLength={1}
					value={value[index] || ''}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
					onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
					onPaste={handlePaste}
					onFocus={handleFocus}
					disabled={disabled}
					style={getOtpDigitBoxStyles(hasError)}
					aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
				/>
			))}
		</div>
	);
};

export default OtpInput;
